import React from "react"
import moment from "moment"
import { Grid, Typography } from "@mui/material"
import { styled } from "@mui/material/styles"
import { has } from "ramda"
import { isValid, parseISO } from "date-fns"

import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded"
import CancelRounded from "@mui/icons-material/CancelRounded"
import ErrorRounded from "@mui/icons-material/ErrorRounded"
import PlayCircleFilledWhiteRounded from "@mui/icons-material/PlayCircleFilledWhiteRounded"

const { vehicleKeys } = require("@carpaddy-fixtures/index")

const ExpiredText = styled(Typography)(
  ({ theme }) => `
    display: flex;
    color: ${theme.palette.error.main};
    font-size: 0.8rem;
    `
)

const WarningText = styled(Typography)(
  ({ theme }) => `
    display: flex;
    color: ${theme.palette.warning.main};
    font-size: 0.8rem;
    `
)

const GreenText = styled(Typography)(
  ({ theme }) => `
    display: flex;
    color: ${theme.palette.success.main};
    font-size: 0.8rem;
    `
)

const GrayText = styled(Typography)(
  ({ theme }) => `
    display: flex;
    color: ${theme.palette.grey["700"]};
    font-size: 0.8rem;
    `
)

const CustomGrid = styled(Grid)`
  display: flex;
  align-items: center;
`

interface ColoredTextProps {
  paper: {
    expiryDate: string
    processing?: boolean
  }
  Icon?: boolean
  showRenewalQty?: boolean
}

interface VehicleRowProps {
  paper: { key: string; expiryDate: string }
}

export const ColouredText = ({
  paper,
  showRenewalQty,
  Icon,
}: ColoredTextProps) => {
  // if document is being processed
  if (has("processing")(paper)) {
    return (
      <GrayText>
        {Icon ? (
          <PlayCircleFilledWhiteRounded sx={{ fontSize: "15px" }} />
        ) : (
          "Processing"
        )}
      </GrayText>
    )
  }

  // if document have expiryDate = null (when being registered newly)
  if (
    paper?.expiryDate === null ||
    paper?.expiryDate.trim() === "" ||
    !isValid(parseISO(paper?.expiryDate))
  ) {
    return (
      <GrayText>
        {Icon ? <CancelRounded sx={{ fontSize: "15px" }} /> : "No expiry date"}
      </GrayText>
    )
  }

  const today = moment()
  const expiryDate = moment(paper.expiryDate, "YYYY-MM-DD")
  const formattedExpiryDate = moment(paper.expiryDate, "YYYY-MM-DD").format(
    "DD/MM/YYYY"
  )
  const diff = expiryDate.diff(today, "days")
  const renewalQty = (expiryPeriod: number) => {
    if (expiryPeriod < 1) {
      return Math.ceil((expiryPeriod * -1) / 365) || 1
    }
  }

  if (diff > 14) {
    return (
      <GreenText>
        {Icon ? (
          <CheckCircleRounded sx={{ fontSize: "15px" }} />
        ) : (
          `${diff} days left`
        )}
      </GreenText>
    )
  } else if (diff < 15 && diff > 0) {
    return (
      <WarningText>
        {Icon ? (
          <ErrorRounded sx={{ fontSize: "15px" }} />
        ) : (
          `Expires in ${diff} days`
        )}
      </WarningText>
    )
  } else {
    return (
      <ExpiredText>
        {Icon ? (
          <CancelRounded sx={{ fontSize: "15px" }} />
        ) : (
          `Expired ${formattedExpiryDate} ${
            showRenewalQty ? ` (${renewalQty(diff)} pcs required)` : ""
          }`
        )}
      </ExpiredText>
    )
  }
}

export const VehicleRow = ({ paper }: VehicleRowProps) => {
  return (
    <CustomGrid container spacing={0.5}>
      <CustomGrid item xs={6}>
        <Typography> {vehicleKeys()[paper.key]}</Typography>
      </CustomGrid>
      <CustomGrid item xs={5}>
        <ColouredText paper={paper} />
      </CustomGrid>
      <CustomGrid item xs={1}>
        <ColouredText paper={paper} Icon />
      </CustomGrid>
    </CustomGrid>
  )
}

export default VehicleRow
